import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { academicService, enrollmentService, userService } from '../../services/api';
import api from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { Card, Badge, Button, Modal, Input, EmptyState, Spinner, Pagination, StatCard } from '../../components/ui';
import { formatPrice } from '../../utils/currency';
import CourseStatsModal from '../../components/CourseStatsModal';
import CourseCommentsModal from '../../components/CourseCommentsModal';

const PAGE_SIZE = 20;

function AdminClasses() {
  const { addNotification } = useNotification();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [modules, setModules] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [platformStats, setPlatformStats] = useState(null);

  const [rosterModule, setRosterModule] = useState(null);
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);

  const [teacherModule, setTeacherModule] = useState(null);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState([]);
  const [savingTeachers, setSavingTeachers] = useState(false);

  const [statsModule, setStatsModule] = useState(null);
  const [commentsModule, setCommentsModule] = useState(null);

  const fetchModules = useCallback(() => {
    setLoading(true);
    academicService.getModules({ page, page_size: PAGE_SIZE, search: search || undefined, include_inactive: true })
      .then((res) => {
        const data = res.data;
        setModules(Array.isArray(data) ? data : data.results || []);
        setCount(Array.isArray(data) ? data.length : data.count || 0);
      })
      .catch(() => addNotification(t('Failed to load classes'), 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  useEffect(() => {
    fetchModules();
    api.get('/users/', { params: { role: 'teacher', page_size: 100 } }).then((res) => {
      const data = res.data;
      setTeachers(Array.isArray(data) ? data : data.results || []);
    }).catch(() => {});
    userService.getDashboard().then((res) => setPlatformStats(res.data.stats)).catch(() => {});
  }, [fetchModules]);

  const filteredModules = modules.filter((m) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return m.name.toLowerCase().includes(q) || m.code.toLowerCase().includes(q);
  });

  const openRoster = async (module) => {
    setRosterModule(module);
    setRosterLoading(true);
    try {
      const res = await enrollmentService.getModuleEnrollments(module.id, 'active');
      const data = res.data;
      setRoster(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      addNotification(t('Failed to load roster'), 'error');
      setRoster([]);
    } finally {
      setRosterLoading(false);
    }
  };

  const openTeacherModal = (module) => {
    setTeacherModule(module);
    setSelectedTeacherIds((module.teachers || []).map((tch) => tch.id));
  };

  const toggleTeacherSelection = (teacherId) => {
    setSelectedTeacherIds((prev) =>
      prev.includes(teacherId) ? prev.filter((id) => id !== teacherId) : [...prev, teacherId]
    );
  };

  const saveTeachers = async () => {
    if (selectedTeacherIds.length === 0) {
      addNotification(t('Select at least one teacher'), 'error');
      return;
    }
    setSavingTeachers(true);
    try {
      const res = await academicService.updateModule(teacherModule.id, { teachers: selectedTeacherIds });
      setModules((prev) => prev.map((m) => (m.id === teacherModule.id ? res.data : m)));
      addNotification(t('Teachers updated'), 'success');
      setTeacherModule(null);
    } catch (e) {
      addNotification(t('Failed to update teachers'), 'error');
    } finally {
      setSavingTeachers(false);
    }
  };

  const removeClass = async (module) => {
    if (!(await confirm(t('Delete {{name}}? This cannot be undone.', { name: module.name })))) return;
    try {
      await academicService.deleteModule(module.id);
      setModules((prev) => prev.filter((m) => m.id !== module.id));
      addNotification(t('Class deleted'), 'success');
    } catch (e) {
      addNotification(t('Failed to delete class'), 'error');
    }
  };


  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Classes')} subtitle={t('Manage classes, teachers, and student rosters')} />
        <main className="flex-1 overflow-y-auto p-6">
          {platformStats && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
              <StatCard label={t('Courses')} value={platformStats.total_modules ?? 0} color="blue" />
              <StatCard label={t('Active Enrollments')} value={platformStats.active_enrollments ?? 0} color="green" />
              <StatCard label={t('Lessons')} value={platformStats.total_lessons ?? 0} color="purple" />
              <StatCard label={t('Quizzes')} value={platformStats.total_quizzes ?? 0} color="yellow" />
              <StatCard label={t('Questions')} value={platformStats.total_discussion_threads ?? 0} color="red" />
            </div>
          )}

          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <Input
              placeholder={t('Search by name or code...')}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="sm:max-w-xs"
            />
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={() => navigate('/admin/sponsorships')}>{t('Manage Sponsorships')}</Button>
              <Button onClick={() => navigate('/modules/create')}>+ {t('Create Class')}</Button>
            </div>
          </div>

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : filteredModules.length === 0 ? (
            <Card><EmptyState title={t('No classes yet')} action={<Button onClick={() => navigate('/modules/create')}>+ {t('Create Class')}</Button>} /></Card>
          ) : (
            <Card padded={false} className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">{t('Name')}</th>
                      <th className="text-left px-4 py-3">{t('Grade Level')}</th>
                      <th className="text-left px-4 py-3">{t('Teacher(s)')}</th>
                      <th className="text-left px-4 py-3">{t('Students')}</th>
                      <th className="text-left px-4 py-3">{t('Price')}</th>
                      <th className="text-left px-4 py-3">{t('Sponsored')}</th>
                      <th className="text-left px-4 py-3">{t('Comments')}</th>
                      <th className="text-right px-4 py-3">{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredModules.map((m) => (
                      <tr key={m.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{m.name}</div>
                          <div className="text-xs text-gray-400">{m.code}</div>
                          <button
                            type="button"
                            onClick={() => setStatsModule(m)}
                            className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-primary-50 px-2.5 py-1.5 text-xs font-semibold text-primary-700 hover:bg-primary-100 transition"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            {t('View Stats')}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(m.grades || []).map((g) => <Badge key={g.id} tone="blue">{g.name}</Badge>)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {(m.teachers || []).length === 0 ? (
                            <span className="text-amber-600 text-xs font-medium">{t('No teacher assigned')}</span>
                          ) : (
                            (m.teachers || []).map((tch) => `${tch.first_name} ${tch.last_name}`).join(', ')
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{m.students_count ?? 0}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {m.price && Number(m.price) > 0 ? formatPrice(m.price, m.currency) : t('Free')}
                        </td>
                        <td className="px-4 py-3">
                          {m.is_sponsored ? (
                            <Badge tone="yellow">★ {t('Sponsored')}</Badge>
                          ) : m.pending_sponsorship ? (
                            <Badge tone="blue">{t('Request pending')}</Badge>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => setCommentsModule(m)}
                            className="inline-flex items-center gap-1.5 text-gray-600 hover:text-primary-700 font-medium"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                            </svg>
                            {m.discussion_threads_count ?? 0}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                          <Button size="sm" variant="secondary" onClick={() => openRoster(m)}>{t('View Roster')}</Button>
                          <Button size="sm" variant="secondary" onClick={() => openTeacherModal(m)}>{t('Assign Teachers')}</Button>
                          <Button size="sm" variant="secondary" onClick={() => navigate(`/modules/${m.id}/edit`)}>{t('Edit')}</Button>
                          <Button size="sm" variant="danger" onClick={() => removeClass(m)}>{t('Delete')}</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination page={page} count={count} pageSize={PAGE_SIZE} onPageChange={setPage} />
            </Card>
          )}
        </main>
      </div>

      {/* Roster modal */}
      <Modal open={!!rosterModule} onClose={() => setRosterModule(null)} title={t('Class Roster')} size="lg">
        {rosterModule && (
          <>
            <p className="text-sm text-gray-500 mb-4">{rosterModule.name} · {rosterModule.code}</p>
            {rosterLoading ? (
              <div className="py-8 flex justify-center"><Spinner /></div>
            ) : roster.length === 0 ? (
              <EmptyState title={t('No students enrolled yet')} />
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {roster.map((enr) => (
                  <div key={enr.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2">
                    <div>
                      <div className="font-medium text-gray-900 text-sm">{enr.student?.first_name} {enr.student?.last_name}</div>
                      <div className="text-xs text-gray-500">{enr.student?.email}</div>
                    </div>
                    <div className="text-xs text-gray-400">{enr.grade_name}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Teacher assignment modal */}
      <Modal
        open={!!teacherModule}
        onClose={() => setTeacherModule(null)}
        title={t('Assign Teachers')}
        footer={<>
          <Button variant="secondary" onClick={() => setTeacherModule(null)}>{t('Cancel')}</Button>
          <Button loading={savingTeachers} onClick={saveTeachers}>{t('Save')}</Button>
        </>}
      >
        {teacherModule && (
          <>
            <p className="text-sm text-gray-500 mb-4">{teacherModule.name} · {teacherModule.code}</p>
            <div className="border border-gray-200 rounded-lg divide-y max-h-72 overflow-y-auto">
              {teachers.length === 0 ? (
                <p className="text-sm text-gray-500 p-3">{t('No approved teachers available')}</p>
              ) : (
                teachers.map((tch) => (
                  <label key={tch.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedTeacherIds.includes(tch.id)}
                      onChange={() => toggleTeacherSelection(tch.id)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{tch.first_name} {tch.last_name} <span className="text-gray-400">({tch.email})</span></span>
                  </label>
                ))
              )}
            </div>
          </>
        )}
      </Modal>

      <CourseStatsModal
        open={!!statsModule}
        onClose={() => setStatsModule(null)}
        moduleId={statsModule?.id}
        moduleName={statsModule?.name}
      />

      <CourseCommentsModal
        open={!!commentsModule}
        onClose={() => setCommentsModule(null)}
        moduleId={commentsModule?.id}
        moduleName={commentsModule?.name}
      />
    </div>
  );
}

export default AdminClasses;
